import { filterClasses } from "@swivl-mono/helpers";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { lockableClick } from "./fields/TicketPropertyRow";

interface EditableDescriptionProps {
  value: string | null;
  onChange: (newValue: string | null) => void;
  pending?: boolean;
  locked?: boolean;
}

export function EditableDescription({ value, onChange, pending, locked }: EditableDescriptionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    const len = e.currentTarget.value.length;
    e.currentTarget.setSelectionRange(len, len);
  };

  useEffect(() => {
    // Don't clobber an in-progress edit; resync on exit.
    if (isEditing) return;
    setEditValue(value ?? "");
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing) {
      autoResize();
    }
  }, [isEditing, autoResize]);

  const handleSubmit = (): void => {
    const trimmed = editValue.trim();
    const newVal = trimmed || null;
    if (newVal !== value) {
      onChange(newVal);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      setEditValue(value ?? "");
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="mb-8 flex cursor-text flex-col rounded border border-orange-500 bg-grey-50 px-3 py-2 ring-2 ring-orange-100">
        <textarea
          ref={textareaRef}
          value={editValue}
          autoFocus
          onFocus={handleFocus}
          onChange={(e) => {
            setEditValue(e.target.value);
            autoResize();
          }}
          onBlur={handleSubmit}
          onKeyDown={handleKeyDown}
          placeholder="Add a description..."
          className="body-sm w-full resize-y bg-transparent text-black outline-none"
        />
      </div>
    );
  }

  return (
    <div
      className={filterClasses(
        "mb-8 flex flex-col rounded border border-transparent bg-grey-50 px-3 py-2",
        locked ? "cursor-not-allowed" : "cursor-text hover:border-(--color-orange-500)"
      )}
    >
      <div onClick={lockableClick(locked, "Updating descriptions", () => setIsEditing(true))} className="group">
        {value ? (
          <p className="body-sm break-words whitespace-pre-wrap text-black">{value}</p>
        ) : (
          <p className="body-sm text-grey-500">Click to add a description...</p>
        )}
        {pending && <span className="ml-2 text-xs text-grey-500">saving...</span>}
      </div>
    </div>
  );
}
